import { useState, useRef, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { startLogin, checkLogin, buildMaxAuthUrl } from '@/api/auth'
import { useAuth } from '@/auth/AuthContext'

const POLL_INTERVAL = 2000
const POLL_TIMEOUT = 6 * 60 * 1000 // 6 минут (бэк держит токен 5)

type Status = 'idle' | 'waiting' | 'success' | 'expired' | 'error'

export function LoginPage() {
  const navigate = useNavigate()
  const { refetch } = useAuth()
  const [searchParams] = useSearchParams()
  const magicExpired = searchParams.get('error') === 'magic-expired'

  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState<string>('')
  const [authUrl, setAuthUrl] = useState<string>('')

  const pollTimerRef = useRef<number | null>(null)
  const timeoutTimerRef = useRef<number | null>(null)

  const stopPolling = () => {
    if (pollTimerRef.current) {
      window.clearInterval(pollTimerRef.current)
      pollTimerRef.current = null
    }
    if (timeoutTimerRef.current) {
      window.clearTimeout(timeoutTimerRef.current)
      timeoutTimerRef.current = null
    }
  }

  useEffect(() => stopPolling, [])

  const handleStartLogin = async () => {
    setError('')
    setStatus('waiting')
    try {
      const { loginToken } = await startLogin()
      const url = buildMaxAuthUrl(loginToken)
      setAuthUrl(url)
      window.open(url, '_blank')

      const startedAt = Date.now()

      // Polling
      pollTimerRef.current = window.setInterval(async () => {
        try {
          const res = await checkLogin(loginToken)
          if (res.status === 'success') {
            stopPolling()
            setStatus('success')
            // Cookie уже поставлен бэком — обновляем юзера и редиректим
            await refetch()
            navigate('/dashboard', { replace: true })
          }
        } catch (e: unknown) {
          // 404 / истечение токена
          const status = (e as { status?: number })?.status
          if (status === 404) {
            stopPolling()
            setStatus('expired')
          }
        }
        if (Date.now() - startedAt > POLL_TIMEOUT) {
          stopPolling()
          setStatus('expired')
        }
      }, POLL_INTERVAL)

      // Hard timeout
      timeoutTimerRef.current = window.setTimeout(() => {
        stopPolling()
        setStatus((prev) => (prev === 'waiting' ? 'expired' : prev))
      }, POLL_TIMEOUT)
    } catch (e) {
      setStatus('error')
      setError(e instanceof Error ? e.message : 'Ошибка запуска входа')
    }
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <img src="/assets/tgx-logo.svg" alt="Telegraphyx" className="login-logo" />
        <h1 className="login-title">Личный кабинет</h1>
        <p className="login-sub">Войдите через MAX-бота, чтобы получить доступ к аналитике каналов</p>

        {magicExpired && status === 'idle' && (
          <div className="login-magic-warn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v4M12 16v.01" />
            </svg>
            <span>
              <b>Ссылка устарела или уже использована.</b> Запросите свежее приглашение или войдите через MAX-бота.
            </span>
          </div>
        )}

        {status === 'idle' && (
          <button className="login-btn-primary" onClick={handleStartLogin}>
            Войти через MAX
          </button>
        )}

        {status === 'waiting' && (
          <div className="login-waiting">
            <div className="login-spinner" />
            <p className="login-waiting-text">
              Откройте MAX и подтвердите вход у бота <strong>TeleGraphyx бот продвижения</strong>
            </p>
            <a className="login-link" href={authUrl} target="_blank" rel="noreferrer">
              Открыть MAX ещё раз
            </a>
            <button
              className="login-btn-secondary"
              onClick={() => {
                stopPolling()
                setStatus('idle')
              }}
            >
              Отмена
            </button>
          </div>
        )}

        {status === 'expired' && (
          <div>
            <p className="login-error">Время ожидания истекло.</p>
            <button className="login-btn-primary" onClick={handleStartLogin}>
              Попробовать снова
            </button>
          </div>
        )}

        {status === 'error' && (
          <div>
            <p className="login-error">{error || 'Не удалось запустить вход.'}</p>
            <button className="login-btn-primary" onClick={handleStartLogin}>
              Попробовать снова
            </button>
          </div>
        )}
      </div>

      <style>{`
        .login-page {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          padding: 24px;
          background: var(--tg-bg, #fafafa);
        }
        .login-card {
          width: 100%;
          max-width: 420px;
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line, #e8e8ec);
          border-radius: 16px;
          padding: 40px 32px;
          text-align: center;
        }
        .login-logo {
          height: 32px;
          margin-bottom: 24px;
        }
        .login-title {
          font-size: 24px;
          font-weight: 700;
          margin: 0 0 8px;
        }
        .login-sub {
          color: var(--tg-text-muted, #6b7280);
          font-size: 14px;
          margin: 0 0 32px;
          line-height: 1.5;
        }
        .login-btn-primary {
          width: 100%;
          height: 48px;
          background: var(--tg-accent, #0066ff);
          color: #fff;
          border: none;
          border-radius: 10px;
          font-size: 15px;
          font-weight: 600;
        }
        .login-btn-primary:hover { opacity: 0.9; }
        .login-btn-secondary {
          width: 100%;
          height: 40px;
          background: transparent;
          color: var(--tg-text-muted, #6b7280);
          border: 1px solid var(--tg-line, #e8e8ec);
          border-radius: 10px;
          font-size: 14px;
          margin-top: 12px;
        }
        .login-waiting { display: flex; flex-direction: column; align-items: center; gap: 12px; }
        .login-spinner {
          width: 32px; height: 32px;
          border: 3px solid var(--tg-line, #e8e8ec);
          border-top-color: var(--tg-accent, #0066ff);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        .login-waiting-text { font-size: 14px; line-height: 1.5; color: var(--tg-text, #111); margin: 0; }
        .login-link {
          color: var(--tg-accent, #0066ff);
          text-decoration: underline;
          font-size: 13px;
        }
        .login-error { color: var(--tg-destructive, #d33); font-size: 14px; margin-bottom: 16px; }
        .login-magic-warn {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 12px 14px;
          background: rgba(255, 152, 0, 0.10);
          border: 1px solid rgba(255, 152, 0, 0.32);
          border-radius: 10px;
          font-size: 13px;
          color: var(--tg-ink-2, #333);
          line-height: 1.5;
          margin-bottom: 16px;
          text-align: left;
        }
        .login-magic-warn svg { width: 18px; height: 18px; color: rgb(180, 130, 0); flex-shrink: 0; margin-top: 1px; }
        .login-magic-warn b { color: var(--tg-ink, #111); font-weight: 700; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  )
}
